import type { Calibration, MarkupShape } from "./markupTypes";

/**
 * Equal as persisted markup: JSONB reorders object keys, and an omitted key persists as one set
 * to `undefined`. The one equality for markup content: the conflict merge, a save's confirmation
 * and the published-content checks (`markupStore`).
 */
export function sameMarkupJson(a: unknown, b: unknown): boolean {
    if (Object.is(a, b)) return true;
    if (Array.isArray(a) || Array.isArray(b)) {
        return (
            Array.isArray(a) &&
            Array.isArray(b) &&
            a.length === b.length &&
            a.every((value, index) => sameMarkupJson(value, b[index]))
        );
    }
    if (typeof a !== "object" || a === null || typeof b !== "object" || b === null) {
        return false;
    }
    const aRecord = a as Record<string, unknown>;
    const bRecord = b as Record<string, unknown>;
    const aKeys = Object.keys(aRecord).filter((key) => aRecord[key] !== undefined);
    const bKeys = Object.keys(bRecord).filter((key) => bRecord[key] !== undefined);
    return (
        aKeys.length === bKeys.length &&
        aKeys.every(
            (key) =>
                Object.prototype.hasOwnProperty.call(bRecord, key) &&
                sameMarkupJson(aRecord[key], bRecord[key]),
        )
    );
}

/** Rebase a full snapshot using its recorded baseline. Remote-only fields and shapes
 * survive; concurrent edits/deletes to the same known shape stop for user recovery. */
export function mergeMarkupIntent(
    baseline: MarkupShape[] | null,
    local: MarkupShape[],
    remote: MarkupShape[],
): { shapes: MarkupShape[]; conflict: boolean } {
    if (!baseline) return { shapes: remote, conflict: true };
    const base = new Map(baseline.map((shape) => [shape.id, shape]));
    const localById = new Map(local.map((shape) => [shape.id, shape]));
    const remoteById = new Map(remote.map((shape) => [shape.id, shape]));
    const result: MarkupShape[] = [];
    for (const remoteShape of remote) {
        const before = base.get(remoteShape.id);
        const intent = localById.get(remoteShape.id);
        if (!before) {
            if (intent && !sameMarkupJson(intent, remoteShape))
                return { shapes: remote, conflict: true };
            result.push(remoteShape); // remote addition
            continue;
        }
        if (!intent) {
            if (!sameMarkupJson(remoteShape, before)) return { shapes: remote, conflict: true };
            continue; // local delete of unchanged baseline
        }
        const localRecord = intent as unknown as Record<string, unknown>;
        const baseRecord = before as unknown as Record<string, unknown>;
        const remoteRecord = remoteShape as unknown as Record<string, unknown>;
        const knownKeys = new Set([...Object.keys(baseRecord), ...Object.keys(localRecord)]);
        const localChanged = [...knownKeys].some(
            (key) => !sameMarkupJson(localRecord[key], baseRecord[key]),
        );
        const remoteChanged = [...knownKeys].some(
            (key) => !sameMarkupJson(remoteRecord[key], baseRecord[key]),
        );
        const sameChange = [...knownKeys].every((key) =>
            sameMarkupJson(localRecord[key], remoteRecord[key]),
        );
        if (localChanged && remoteChanged && !sameChange) return { shapes: remote, conflict: true };
        // Apply only this client's changes, retaining unknown remote fields and
        // honoring removed optional properties as well as added ones.
        const merged = { ...remoteRecord };
        for (const key of knownKeys) {
            if (sameMarkupJson(localRecord[key], baseRecord[key])) continue;
            if (key in localRecord) merged[key] = localRecord[key];
            else delete merged[key];
        }
        result.push(merged as unknown as MarkupShape);
    }
    for (const localShape of local) {
        const before = base.get(localShape.id);
        if (!before && !remoteById.has(localShape.id)) result.push(localShape);
        // A remote deletion cannot silently eat a concurrent local edit.
        if (before && !remoteById.has(localShape.id) && !sameMarkupJson(localShape, before)) {
            return { shapes: remote, conflict: true };
        }
    }
    return { shapes: result, conflict: false };
}

/** Calibration is one value: incompatible concurrent scale edits need recovery. */
export function mergeMarkupCalibration(
    baseline: { calibration: Calibration | null } | null,
    local: Calibration | null,
    remote: Calibration | null,
): { calibration: Calibration | null; conflict: boolean } {
    if (!baseline) return { calibration: remote, conflict: !sameMarkupJson(local, remote) };
    const localChanged = !sameMarkupJson(local, baseline.calibration);
    const remoteChanged = !sameMarkupJson(remote, baseline.calibration);
    return {
        calibration: localChanged ? local : remote,
        conflict: localChanged && remoteChanged && !sameMarkupJson(local, remote),
    };
}
